import { createContext, MutableRefObject, ReactNode, useEffect, useRef, useState } from "react";

import { KeycloakLoginOptions, KeycloakLogoutOptions, KeycloakPromise } from "keycloak-js";

import { Role } from "../enums/Role.ts";
import { ErrorMessage } from "../models/ErrorMessage.ts";
import { ErrorPage } from "../pages/ErrorPage.tsx";
import classes from "../styles/keycloak/KeycloakAuthenticator.module.scss";
import { getMessagingRole } from "../utils/domain/RoleDomain.ts";
import { keycloakInstance } from "./KeycloakConfig.ts";
import { setAuthorization } from "./authorizationHeader";

interface KeycloakAuthenticatorProps {
  children: ReactNode;
}

export interface KeycloakContext {
  tokenRef?: MutableRefObject<string | undefined>;
  roles: string[];

  logout(options?: KeycloakLogoutOptions): KeycloakPromise<void, void>;

  login(options?: KeycloakLoginOptions): KeycloakPromise<void, void>;
}

const initKeycloak = {
  logout: keycloakInstance.logout,
  login: keycloakInstance.login,
  tokenRef: undefined,
  roles: []
};
export const KeycloakAuthenticatorContext = createContext<KeycloakContext>(initKeycloak);

const KeycloakAuthenticator = ({ children }: KeycloakAuthenticatorProps) => {
  const [isLoading, setIsLoading] = useState(false);
  let roles: string[] = [];
  const [errorMessage, setErrorMessage] = useState<ErrorMessage>();
  const tokenRef = useRef(keycloakInstance.token);
  keycloakInstance.onTokenExpired = () => {
    keycloakInstance
      .updateToken(1)
      .then(() => {
        tokenRef.current = keycloakInstance.token;
        setAuthorization(keycloakInstance.token);
      })
      .catch(() => {
        keycloakInstance.login();
      });
  };

  keycloakInstance.onAuthLogout = () => {
    keycloakInstance.login();
  };

  useEffect(() => {
    keycloakInstance
      .init({
        onLoad: "check-sso"
      })
      .then((authenticated) => {
        if (authenticated) {
          roles = getMessagingRole(keycloakInstance.realmAccess?.roles ?? []);
          if (roles.includes(Role.USER)) {
            tokenRef.current = keycloakInstance.token;
            setAuthorization(keycloakInstance.token);
            setIsLoading(true);
          } else {
            setErrorMessage({
              code: 403,
              title: "Unauthorized access",
              description:
                "You do not have access to this resource yet. Please contact a Messaging administrator if you believe you should have access."
            });
          }
        } else {
          keycloakInstance.login();
        }
      })
      .catch(() => {
        keycloakInstance.login();
      });
  }, []);
  if (errorMessage) {
    return (
      <>
        <ErrorPage
          code={errorMessage.code}
          title={errorMessage.title}
          description={errorMessage.description}
        />
        <span className={classes.logout} onClick={() => keycloakInstance.logout()}>
          Logout
        </span>
      </>
    );
  }
  return (
    <>
      {isLoading ? (
        <KeycloakAuthenticatorContext.Provider
          value={{
            ...initKeycloak,
            tokenRef,
            roles
          }}
        >
          {children}
        </KeycloakAuthenticatorContext.Provider>
      ) : (
        <p>Loading...</p>
      )}
    </>
  );
};

export default KeycloakAuthenticator;
